/*
    Copyright 2022-2026, Techwise Integrated Security, LLC
*/

import { Dispatch } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import { deleteHandler, getHandlerVersionCaching, postHandler, stdErrHandlerFactory } from "../../utils/Requests";
import {
    CreateServiceLocationRequest,
    CreateSiteRequest,
    Effect,
    Reference,
    ServiceLocation,
    Setter,
    VersionedPagination,
} from "../../utils/Interfaces";
import { setExistingSites } from "./workSitesSlice";
import { AxiosResponse } from "axios";
import { store } from "../../app/store";

export function deleteSite(siteId: number, dispatch: Dispatch<AnyAction>, onCompanyDeleted: Effect) {
    deleteHandler(`/api/sites/delete/${siteId}`, dispatch, onCompanyDeleted, stdErrHandlerFactory(dispatch));
}

export function updateSites(dispatch: Dispatch<AnyAction>, setLoading: Setter<boolean>) {
    setLoading(true);

    getHandlerVersionCaching<ServiceLocation>(
        "/api/sites/list",
        store.getState().sites.existingSitesVersion,
        dispatch,
        (response: AxiosResponse<VersionedPagination<ServiceLocation>, any>) => {
            dispatch(setExistingSites(response.data));
        },
        () => {},
        stdErrHandlerFactory(dispatch),
        setLoading
    );
}

export function editSite(site: ServiceLocation, dispatch: Dispatch<AnyAction>, onSiteUpdated: Effect) {
    const editSitePayload: CreateSiteRequest = {
        store_no: site.store_no,
        street_address: site.street_address,
        city: site.city,
        state: site.state,
        company: site.company.id,
        zip_code: site.zip_code,
        comments: site.comments,
        store_type: site.store_type,
    };

    postHandler<CreateServiceLocationRequest, Reference>(
        `/api/sites/update/${site.id}`,
        editSitePayload,
        dispatch,
        onSiteUpdated,
        stdErrHandlerFactory(dispatch)
    );
}

export function createSite(site: ServiceLocation, dispatch: Dispatch<AnyAction>, onSiteCreated: Effect) {
    const createSitePayload: CreateServiceLocationRequest = {
        store_no: site.store_no,
        street_address: site.street_address,
        city: site.city,
        state: site.state,
        company: site.company.id,
        zip_code: site.zip_code,
        comments: site.comments,
        store_type: site.store_type,
    };

    postHandler<CreateServiceLocationRequest, Reference>(
        "/api/sites/create",
        createSitePayload,
        dispatch,
        onSiteCreated,
        stdErrHandlerFactory(dispatch)
    );
}

export function storeNoExists(storeNo: string, existingSites: ServiceLocation[]) {
    return existingSites.filter((site) => site.store_no.trim() === storeNo.trim()).length > 0;
}

export function sitesEqual(changed: ServiceLocation, existing: ServiceLocation) {
    return (
        changed.store_no.trim() === existing.store_no &&
        changed.street_address.trim() === existing.street_address &&
        changed.city.trim() === existing.city &&
        changed.state.trim() === existing.state &&
        changed.zip_code?.toString().trim() === existing.zip_code?.toString() &&
        changed.company.id === existing.company.id &&
        changed.store_type?.trim() === existing.store_type &&
        changed.comments?.trim() === existing.comments
    );
}

function validateRequiredSiteFields(site: ServiceLocation): string {
    let enumeratedFields = [
        { field: site.store_no, name: "Store #" },
        { field: site.street_address, name: "Street Address" },
        { field: site.city, name: "City" },
        { field: site.store_type, name: "Store Type" },
    ];

    let error: string = "";

    enumeratedFields.map((entry): string => {
        if (!entry.field?.trim()) {
            error = `The ${entry.name} cannot be blank.`;
            return error;
        }
        return error;
    });

    if (site.zip_code === null) {
        return "Zip Code cannot be blank.";
    }

    let zipCodeNumber = Number(site.zip_code as unknown as string);
    if (isNaN(zipCodeNumber) || zipCodeNumber < 10000 || zipCodeNumber > 99999) {
        return "Zip Code must be a number 10000-99999.";
    }

    return error;
}

export function editSiteValidation(site: ServiceLocation, existingSites: ServiceLocation[], originalStoreNo: string): string {
    let error: string = validateRequiredSiteFields(site);
    if (error) {
        return error;
    }

    if (storeNoExists(site.store_no.trim(), existingSites)) {
        let siteEqualsOriginal = sitesEqual(
            site,
            existingSites.filter((existingSite) => existingSite.store_no.trim() === site.store_no.trim())[0]
        );

        if (siteEqualsOriginal) {
            return "No changes detected.";
        }

        // If the store # has been changed, but it collides with another Store #.
        if (site.store_no.trim() !== originalStoreNo.trim()) {
            return `Store #${site.store_no.trim()} already exists.`;
        }
    }

    return "";
}

export function createSiteValidation(site: ServiceLocation, existingSites: ServiceLocation[]): string {
    let error: string = validateRequiredSiteFields(site);
    if (error) {
        return error;
    }

    if (storeNoExists(site.store_no.trim(), existingSites)) {
        return `Store #${site.store_no.trim()} already exists.`;
    }

    return "";
}
